import { User, Layers, Globe, BookOpen, Sparkles, Clapperboard, Lock, FileDown, ArrowLeft, Network, FileText, ScrollText } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useState } from 'react';
import { toast } from 'sonner';
import LanguageSelector from './LanguageSelector';
import UpgradeModal from './UpgradeModal';
import { authFetch } from '../lib/authFetch';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INDIE_AND_ABOVE = ['indie', 'studio'];

export default function Sidebar({
  userTier = 'spark',
  isPro,
  // Workspace mode — pass both to activate
  projectId,
  projectTitle,
  onExportBible,
  exportingBible,
}) {
  const effectiveTier = userTier || (isPro ? 'studio' : 'spark');
  const canAccessPaidFeatures = INDIE_AND_ABOVE.includes(effectiveTier);

  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const [exporting, setExporting] = useState(false);
  const [upgradeModal, setUpgradeModal] = useState(false);

  const currentPath = location.pathname;
  const currentSearch = new URLSearchParams(location.search);
  const currentType = currentSearch.get('type') || 'character';
  const currentTab = currentSearch.get('tab') || 'plot';

  const isWorkspaceMode = Boolean(projectId);

  const handleExportPDF = async () => {
    // If parent provides handler (workspace mode), use it
    if (onExportBible) {
      onExportBible();
      return;
    }
    // Story Bible export is strictly project-scoped — needs a project to export.
    if (!projectId) {
      toast.error('Open a project to export its Story Bible');
      navigate('/dashboard');
      return;
    }
    setExporting(true);
    try {
      const response = await authFetch(
        `${API}/export/story-bible?project_id=${encodeURIComponent(projectId)}`,
        { credentials: 'include' }
      );
      if (!response.ok) throw new Error('Export failed');
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `story_bible_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Story Bible exported successfully!');
      if (!canAccessPaidFeatures) {
        setUpgradeModal(true);
      }
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export PDF');
    } finally {
      setExporting(false);
    }
  };

  /* ── Global tabs (no projectId) ── */
  const GLOBAL_TABS = [
    {
      id: 'character',
      label: t('sidebar_char'),
      Icon: User,
      path: '/dashboard?type=character',
      locked: false,
      comingSoon: false,
    },
    {
      id: 'plot',
      label: t('sidebar_plot'),
      Icon: Layers,
      path: '/dashboard?type=plot',
      locked: false,
      comingSoon: false,
    },
    {
      id: 'world',
      label: t('sidebar_world'),
      Icon: Globe,
      path: '/world-building',
      locked: !canAccessPaidFeatures,
      comingSoon: false,
    },
    {
      id: 'scene',
      label: t('sidebar_scenes'),
      Icon: Clapperboard,
      path: '/scenes',
      locked: false,
      comingSoon: false,
    },
    {
      id: 'bible',
      label: t('sidebar_bible'),
      Icon: BookOpen,
      path: null,
      action: handleExportPDF,
      locked: false,
      comingSoon: false,
    },
    {
      id: 'mentor',
      label: t('sidebar_mentor'),
      Icon: Sparkles,
      path: '/dashboard?type=character',
      locked: !canAccessPaidFeatures,
      comingSoon: false,
    },
  ];

  /* ── Workspace tabs (projectId provided) ── */
  const WORKSPACE_TABS = [
    {
      id: 'plot',
      label: t('sidebar_plot'),
      Icon: Layers,
      tab: 'plot',
      locked: false,
    },
    {
      id: 'characters',
      label: t('sidebar_chars'),
      Icon: User,
      tab: 'characters',
      locked: false,
    },
    {
      id: 'scenes',
      label: t('sidebar_scenes'),
      Icon: Clapperboard,
      tab: 'scenes',
      locked: false,
    },
    {
      id: 'screenplay',
      label: t('sidebar_script'),
      Icon: ScrollText,
      tab: 'screenplay',
      // Script editor is Indie+ on the server (save + export 403 for Spark);
      // lock it here so Spark never opens an editor that cannot save.
      locked: !canAccessPaidFeatures,
    },
    {
      id: 'world',
      label: t('sidebar_world'),
      Icon: Globe,
      tab: 'world',
      locked: !canAccessPaidFeatures,
    },
    {
      id: 'notes',
      label: 'Notes',
      Icon: FileText,
      tab: 'notes',
      locked: false,
    },
    {
      id: 'web',
      label: t('sidebar_web'),
      Icon: Network,
      tab: 'web',
      locked: !canAccessPaidFeatures,
    },
    {
      id: 'bible',
      label: t('sidebar_bible'),
      Icon: BookOpen,
      tab: null,
      action: handleExportPDF,
      locked: false,
    },
  ];

  const isGlobalActive = (tab) => {
    if (tab.id === 'world') return currentPath === '/world-building';
    if (tab.id === 'character') return currentType === 'character' && currentPath.includes('dashboard');
    if (tab.id === 'plot') return currentType === 'plot' && currentPath.includes('dashboard');
    return false;
  };

  const isWorkspaceActive = (tab) => {
    if (!currentPath.includes('/project/')) return false;
    return currentTab === tab.id;
  };

  const handleGlobalClick = (tab) => {
    if (tab.comingSoon) return;
    if (tab.locked) { navigate('/upgrade'); return; }
    if (tab.action) { tab.action(); return; }
    navigate(tab.path);
  };

  const handleWorkspaceClick = (tab) => {
    if (tab.locked) { navigate('/upgrade'); return; }
    if (tab.action) { tab.action(); return; }
    navigate(`/project/${projectId}?tab=${tab.tab}`);
  };

  const isExporting = exportingBible || exporting;

  /* ── Render ── */
  const tabs = isWorkspaceMode ? WORKSPACE_TABS : GLOBAL_TABS;
  const handleClick = isWorkspaceMode ? handleWorkspaceClick : handleGlobalClick;
  const isActive = isWorkspaceMode ? isWorkspaceActive : isGlobalActive;

  return (
    <>
    {/* ── Desktop rail (md and up) ── */}
    <div className="hidden md:flex w-20 bg-card border-r border-border/40 flex-col items-center py-6 gap-1">
      <div className="font-mono text-lg font-bold text-primary mb-4" data-testid="sidebar-logo">
        PJ
      </div>

      {isWorkspaceMode ? (
        <>
          {/* Back button */}
          <div className="w-14 mb-1">
            <button
              onClick={() => navigate('/dashboard?type=plot')}
              data-testid="sidebar-workspace-back"
              title={t('sidebar_backToProjects')}
              className="w-full h-10 flex flex-col items-center justify-center gap-0.5 border-2 border-transparent text-muted-foreground hover:text-foreground hover:border-border transition-all"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-[8px] font-mono uppercase tracking-wide">{t('sidebar_back')}</span>
            </button>
          </div>

          {/* Active project name */}
          {projectTitle && (
            <div
              className="w-14 mb-2 px-1 text-center"
              title={projectTitle}
            >
              <span className="text-[8px] font-mono text-primary/80 leading-tight block"
                style={{ wordBreak: 'break-all', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}
              >
                {projectTitle}
              </span>
            </div>
          )}

          {/* Divider */}
          <div className="w-10 h-px bg-border/40 mb-1" />

          {/* Workspace tabs */}
          {WORKSPACE_TABS.map((tab) => {
            const active = isWorkspaceActive(tab);
            const { Icon } = tab;

            return (
              <div
                key={tab.id}
                className="relative w-14"
                title={tab.locked ? t('sidebar_locked') : tab.label}
              >
                <button
                  onClick={() => handleWorkspaceClick(tab)}
                  data-testid={`sidebar-workspace-tab-${tab.id}`}
                  className={`w-full h-14 flex flex-col items-center justify-center gap-1 transition-all relative
                    ${active
                      ? 'bg-primary/20 border-2 border-primary text-primary'
                      : tab.locked
                      ? 'border-2 border-transparent text-muted-foreground/40 cursor-not-allowed'
                      : 'border-2 border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-[9px] font-mono uppercase tracking-wide">{tab.label}</span>
                  {tab.locked && (
                    <Lock className="w-2.5 h-2.5 absolute bottom-1 right-1 text-muted-foreground/50" />
                  )}
                  {tab.id === 'bible' && isExporting && (
                    <span className="absolute bottom-1 right-1 text-[7px] font-mono text-primary">...</span>
                  )}
                </button>
              </div>
            );
          })}
        </>
      ) : (
        <>
          {GLOBAL_TABS.map((tab) => {
            const active = isGlobalActive(tab);
            const { Icon } = tab;

            return (
              <div key={tab.id} className="relative w-14" title={
                tab.comingSoon
                  ? 'Coming Soon'
                  : tab.locked
                  ? t('sidebar_locked')
                  : tab.label
              }>
                <button
                  onClick={() => handleGlobalClick(tab)}
                  data-testid={`sidebar-tab-${tab.id}`}
                  className={`w-full h-14 flex flex-col items-center justify-center gap-1 transition-all relative
                    ${active
                      ? 'bg-primary/20 border-2 border-primary text-primary'
                      : tab.locked || tab.comingSoon
                      ? 'border-2 border-transparent text-muted-foreground/40 cursor-not-allowed'
                      : 'border-2 border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="text-[9px] font-mono uppercase tracking-wide">{tab.label}</span>

                  {tab.locked && !tab.comingSoon && (
                    <Lock className="w-2.5 h-2.5 absolute bottom-1 right-1 text-muted-foreground/50" />
                  )}
                </button>

                {tab.comingSoon && (
                  <div className="absolute -top-1 -right-1 bg-muted text-muted-foreground font-mono text-[7px] uppercase px-1 py-0.5 leading-none border border-border/60">
                    SOON
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}

      <div className="flex-1" />

      {/* Language Selector — constrained to sidebar width to prevent overflow */}
      <div className="mb-2 w-14 flex items-center justify-center overflow-hidden" data-testid="sidebar-language">
        <LanguageSelector userTier={effectiveTier} />
      </div>

      {upgradeModal && (
        <UpgradeModal
          title="Remove the watermark. Own your story."
          description="You've exported your first Story Bible. Upgrade to get a clean, professional, branded PDF."
          benefits={[
            'No watermark on exported PDFs',
            'Modular section exports',
            'Courier Prime professional formatting',
            'Branded cover page',
          ]}
          ctaText="Upgrade Now"
          onClose={() => setUpgradeModal(false)}
        />
      )}
    </div>

    {/* ── Mobile bottom nav (below md) ── */}
    <nav
      className="md:hidden fixed bottom-0 inset-x-0 z-40 bg-card border-t border-border/40 flex items-stretch justify-around"
      style={{ paddingBottom: 'env(safe-area-inset-bottom)' }}
      data-testid="sidebar-mobile-nav"
      data-mobile-nav-present
    >
      {isWorkspaceMode && (
        <button
          onClick={() => navigate('/dashboard?type=plot')}
          data-testid="sidebar-mobile-back"
          className="flex-1 min-w-0 h-14 flex flex-col items-center justify-center gap-0.5 text-muted-foreground active:bg-primary/10"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="text-[9px] font-mono uppercase tracking-wide">{t('sidebar_back')}</span>
        </button>
      )}
      {tabs.map((tab) => {
        const active = isActive(tab);
        const { Icon } = tab;
        const disabled = tab.comingSoon;
        return (
          <button
            key={tab.id}
            onClick={() => handleClick(tab)}
            data-testid={`sidebar-mobile-tab-${tab.id}`}
            disabled={disabled}
            className={`flex-1 min-w-0 h-14 flex flex-col items-center justify-center gap-0.5 transition-colors relative
              ${active
                ? 'text-primary bg-primary/10'
                : tab.locked || disabled
                ? 'text-muted-foreground/40'
                : 'text-muted-foreground active:bg-primary/10'
              }`}
          >
            <Icon className="w-5 h-5" />
            <span className="text-[9px] font-mono uppercase tracking-wide truncate w-full text-center px-1">{tab.label}</span>
            {tab.locked && !disabled && (
              <Lock className="w-2.5 h-2.5 absolute top-1 right-2 text-muted-foreground/50" />
            )}
            {tab.id === 'bible' && isExporting && (
              <span className="absolute top-1 right-2 text-[7px] font-mono text-primary">...</span>
            )}
          </button>
        );
      })}
    </nav>
    </>
  );
}
